import { AccountGroupListResponseSchema, DeleteAccountGroupResponseSchema, GroupedBalanceSheetSchema, GroupedBalanceSheetSeriesSchema, GroupedIncomeStatementSchema, GroupedIncomeStatementSeriesSchema, GroupedTrialBalanceSchema, LedgerAccountGroupSchema, LedgerAccountGroupResponseSchema, LedgerAccountSchema, } from '@packages/types';
import { api } from '../client.js';
import { asOfSeriesQuery, reportPeriodSetQuery } from './ledger.js';
// ---------------------------------------------------------------------------
// Account-group endpoints (task #198). Presentation-only grouping tree over a
// ledger's chart of accounts - the eight routes in
// `apps/backend/platform/src/api/routes/account-group.ts`. The read side is
// `getGroupedTrialBalance` (the assembled tree, subtotals rolled up on the
// backend) plus `listAccountGroups` (the flat rows that drive the
// structure-editing UI). The six structural mutations are single-purpose.
// ---------------------------------------------------------------------------
const ledgerBase = (accountId, ledgerId) => `/accounts/${accountId}/ledgers/${ledgerId}`;
function periodReportQuery(range) {
    const params = new URLSearchParams();
    if (range.period !== undefined) {
        params.set('period', range.period);
    }
    else {
        params.set('from', range.from);
        params.set('to', range.to);
    }
    return params.toString();
}
function asOfReportQuery(at) {
    const params = new URLSearchParams();
    if (at.asOfPreset !== undefined) {
        params.set('asOfPreset', at.asOfPreset);
    }
    else {
        params.set('asOf', at.asOf);
    }
    return params.toString();
}
/** The v1 grouped presentation of the trial balance (assembled tree + subtotals). */
export function getGroupedTrialBalance(accountId, ledgerId, at, showZeroBalances) {
    const parts = [];
    if (at)
        parts.push(asOfReportQuery(at));
    if (showZeroBalances !== undefined)
        parts.push(`showZeroBalances=${showZeroBalances}`);
    const qs = parts.join('&');
    return api(`${ledgerBase(accountId, ledgerId)}/grouped-trial-balance${qs ? `?${qs}` : ''}`, GroupedTrialBalanceSchema);
}
export function getGroupedIncomeStatement(accountId, ledgerId, range) {
    return api(`${ledgerBase(accountId, ledgerId)}/grouped-income-statement?${periodReportQuery(range)}`, GroupedIncomeStatementSchema);
}
export function getGroupedIncomeStatementSeries(accountId, ledgerId, input) {
    return api(`${ledgerBase(accountId, ledgerId)}/grouped-income-statement-series?${reportPeriodSetQuery(input)}`, GroupedIncomeStatementSeriesSchema);
}
export function getGroupedBalanceSheet(accountId, ledgerId, at) {
    return api(`${ledgerBase(accountId, ledgerId)}/grouped-balance-sheet?${asOfReportQuery(at)}`, GroupedBalanceSheetSchema);
}
/**
 * Serialises a balance-sheet series request: the SAME anchor the single-date
 * report sends, plus what to compare it against. Exported so `report-export.ts`
 * builds its download URLs from one implementation rather than a second,
 * driftable copy - the exports must always request exactly what the screen is
 * showing.
 */
export function getGroupedBalanceSheetSeries(accountId, ledgerId, at, compare) {
    return api(`${ledgerBase(accountId, ledgerId)}/grouped-balance-sheet-series?${asOfSeriesQuery(at, compare)}`, GroupedBalanceSheetSeriesSchema);
}
/** Every group row for a ledger (flat) - the structure-editing UI's read. */
export function listAccountGroups(accountId, ledgerId) {
    return api(`${ledgerBase(accountId, ledgerId)}/account-groups`, AccountGroupListResponseSchema);
}
export function createAccountGroup(accountId, ledgerId, input) {
    return api(`${ledgerBase(accountId, ledgerId)}/account-groups`, LedgerAccountGroupSchema, {
        method: 'POST',
        body: input,
    });
}
export function renameAccountGroup(accountId, ledgerId, groupId, input) {
    return api(`${ledgerBase(accountId, ledgerId)}/account-groups/${groupId}`, LedgerAccountGroupSchema, {
        method: 'PATCH',
        body: input,
    });
}
export function moveAccountGroup(accountId, ledgerId, groupId, input) {
    return api(`${ledgerBase(accountId, ledgerId)}/account-groups/${groupId}/move`, LedgerAccountGroupSchema, {
        method: 'PUT',
        body: input,
    });
}
export function reorderAccountGroups(accountId, ledgerId, input) {
    return api(`${ledgerBase(accountId, ledgerId)}/account-groups/reorder`, AccountGroupListResponseSchema, {
        method: 'PUT',
        body: input,
    });
}
export function deleteAccountGroup(accountId, ledgerId, groupId) {
    return api(`${ledgerBase(accountId, ledgerId)}/account-groups/${groupId}`, DeleteAccountGroupResponseSchema, {
        method: 'DELETE',
    });
}
/** Set (or clear, via `groupId: null`) a single ledger account's group. */
export function setLedgerAccountGroup(accountId, ledgerId, ledgerAccountId, input) {
    return api(`${ledgerBase(accountId, ledgerId)}/accounts/${ledgerAccountId}/group`, LedgerAccountSchema, {
        method: 'PUT',
        body: input,
    });
}
/**
 * The group a single ledger account currently sits in (`{ group: null }` when
 * ungrouped). Deliberately per-account rather than reading the assembled tree:
 * the account detail page shows and edits one account's group, and
 * `getGroupedTrialBalance` computes every account's balance to answer it.
 */
export function getLedgerAccountGroup(accountId, ledgerId, ledgerAccountId) {
    return api(`${ledgerBase(accountId, ledgerId)}/accounts/${ledgerAccountId}/group`, LedgerAccountGroupResponseSchema);
}
